/*
  •	Copyright (c) 2024 Pegasystems Inc.
  •	All rights reserved.
*/
import { ofType } from 'redux-observable';
import { empty, Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { ActionManagerInstance } from '../../actions/manager';
import constants, { publicConstants } from '../../constants';
import { getResolvedTargetName } from '../../router/routing-manager';
import { getTranslatedDXAPIResponse } from '../../utils/dxapi-response-translator';
import { throttleUntilChanged } from '../../utils/rxjs-utils';
import Utils from '../../utils/utils';
import { getRootContainerName, isContainerSingleMode } from '../../container/container-utils';
import {
  addPreviewResetAction,
  dispatchCorrectActionIfChildOrRemote,
  getActionBasedOnContainerMode,
  getActionPayload,
  getContext,
  getDirtyDetails,
  isAddContainerAction,
  processDataToDispatchErrorAction,
  processViewResponse
} from '../../store/state/epics/util';
import { getObjectClassName, getObjectInfoID, updateKeyAndFlowName } from '../common-helper';
import {
  isModalContainer,
  promiseHandler,
  addActionToRemoveContainerItem,
  getDisplayCaseID
} from '../../case/common-helper';
import { getSemanticURL } from '../../router/app-router';
import type { Action } from '../../store/state/state-manager';
import { ERROR, ACTION_CREATE_EXTERNAL_OBJECT } from '../../store/state/actions';
import { evaluateDirtyBeforeDxApiCall, STATE_PATHS } from '../../case/create-work/helper';
import type { ActionsOut } from '../../case/types';
import { setDirectUrlUpdation, isCreateStageActive, getCaseViewMode } from '../create-object/helper';

import { getContainerName } from './helper';
import { executeCreateExternalObjectRequest } from './async-request';
import { CREATE_EXTERNAL_OBJECT } from './action-creator';

const createExternalObject = (action$: Observable<Action>) => {
  let actionMgrID = 0;
  let actionsOut: ActionsOut[] = [];
  const throttleValue = 100;

  return action$.pipe(
    ofType(CREATE_EXTERNAL_OBJECT),
    throttleUntilChanged(throttleValue),
    map((action: Action) => {
      ({ actionMgrID } = action.payload);
      return action.payload;
    }),
    mergeMap((payload) => {
      const { context } = payload;
      const containerName = getContainerName(payload);
      const target = getResolvedTargetName(containerName, context);
      const { continueExecution, removeActionPayload } = evaluateDirtyBeforeDxApiCall(target);

      if (!continueExecution) {
        ActionManagerInstance.rejectActionByID(actionMgrID);
        return empty();
      }

      return executeCreateExternalObjectRequest(payload, { removeActionPayload });
    }),
    mergeMap(async (data) => {
      if (data && data.type === ERROR) {
        data.payload.context = getRootContainerName();
        actionsOut.push(processDataToDispatchErrorAction(data));
        return actionsOut;
      }

      const { actionInPayload, response, headers, removeActionPayload } = data;
      const isInCreateStageRes = isCreateStageActive(actionInPayload, response);
      // When a case is created it will be in first assignment
      Utils.setIn(response.data, constants.getProperty(constants.CASE_INFO.IS_FIRST_ASSIGNMENT), true);
      const containerName = getContainerName({ ...actionInPayload, isInCreateStage: isInCreateStageRes });
      const target = getResolvedTargetName(containerName, actionInPayload.context);
      const context = getContext(target, containerName, actionInPayload.context);

      if (!isModalContainer(containerName) && isContainerSingleMode(target)) {
        const { continueExecution } = getDirtyDetails(target);
        if (!continueExecution) {
          ActionManagerInstance.rejectActionByID(actionMgrID);
          return empty();
        }
      }
      const translatedDXAPIResponse = getTranslatedDXAPIResponse(response);

      let semanticURL = getSemanticURL(ACTION_CREATE_EXTERNAL_OBJECT, {
        caseClassName: getObjectClassName(translatedDXAPIResponse)
      });
      semanticURL = semanticURL.replace(/{.+?}/, String(getDisplayCaseID(getObjectInfoID(translatedDXAPIResponse))));
      translatedDXAPIResponse.semanticURL = semanticURL;

      setDirectUrlUpdation(translatedDXAPIResponse, actionInPayload);

      const options = { acName: target, context };

      await processViewResponse(translatedDXAPIResponse, actionInPayload, options);
      const caseViewMode = getCaseViewMode(translatedDXAPIResponse);

      const commonInfo = {
        target,
        context,
        headers,
        semanticURL,
        caseViewMode,
        containerItemID: context,
        isInCreateStage: isInCreateStageRes,
        isMinimizable: isInCreateStageRes,
        assignmentContext: Utils.getIn(translatedDXAPIResponse, STATE_PATHS.NEXT_ASSIGNMENT_INFO_CONTEXT),
        nextAssignmentInfo: Utils.getIn(translatedDXAPIResponse, STATE_PATHS.NEXT_ASSIGNMENT_INFO),
        resourceType: publicConstants.RESOURCE_TYPES.OBJECT,
        openCaseViewAfterCreate: actionInPayload.openCaseViewAfterCreate,
        modalOptions: actionInPayload.modalOptions,
        debugInfo: actionInPayload.debugInfo,
        isExternalObjectCreationFlow: true
      };

      // updating key and flowName
      updateKeyAndFlowName(commonInfo, translatedDXAPIResponse);
      const payload = getActionPayload(translatedDXAPIResponse, commonInfo);
      const action = getActionBasedOnContainerMode(target, payload);

      if (isAddContainerAction(action)) {
        addActionToRemoveContainerItem(actionsOut, { removeActionPayload, target });
      }

      actionsOut.push(action);
      addPreviewResetAction(actionsOut);
      return actionsOut;
    }),
    map(() => {
      dispatchCorrectActionIfChildOrRemote(actionsOut);
      promiseHandler(actionsOut, actionMgrID);
      actionsOut = [];
      return actionsOut;
    })
  );
};

export default createExternalObject;
